import { config } from '@/data/config';
import { request } from '@/data/http';
import type {
  Agendamento,
  EventoHistorico,
  HorarioDisponivel,
  Instalacao,
  InstalacaoBusca,
  NovidadeInput,
  NovoAgendamento,
  Novidade,
  PanDataSource,
  RetencaoHorario,
} from '@/data/types';
import {
  SEM_ATUALIZACOES_ID,
  toAgendamento,
  toDisponibilidade,
  toEventoHistorico,
  toInstalacao,
  toNovidade,
  toRows,
  toVersoes,
} from './normalize';

/** Monta o instante ISO a partir de data local (YYYY-MM-DD) e hora local (HH:mm). */
export function montarAgendadoPara(data: string, hora: string): string {
  const [ano, mes, dia] = data.split('-').map(Number);
  const [h, m] = hora.split(':').map(Number);
  return new Date(ano, mes - 1, dia, h, m).toISOString();
}

/** Máximo aceito por `/instalacoes/paginado`. */
const TAMANHO_PAGINA_ESCOPO = 200;

export const restDataSource: PanDataSource = {
  kind: 'rest',

  /**
   * Escopo INTEIRO do usuário — percorre todas as páginas de
   * `/instalacoes/paginado`. `GET /instalacoes` sem `limit` devolve só as 50
   * primeiras, o que cortava a lista (e os totais da Home) de quem administra
   * muitas instalações. A 1ª página informa o total; o resto sai em paralelo.
   */
  async listInstalacoes(signal) {
    const primeira = await restDataSource.listInstalacoesPaginado({ limit: TAMANHO_PAGINA_ESCOPO, offset: 0 }, signal);
    const restantes = Math.ceil(primeira.total / TAMANHO_PAGINA_ESCOPO) - 1;
    if (restantes <= 0) return primeira.itens;
    const demais = await Promise.all(
      Array.from({ length: restantes }, (_, indice) =>
        restDataSource.listInstalacoesPaginado(
          { limit: TAMANHO_PAGINA_ESCOPO, offset: (indice + 1) * TAMANHO_PAGINA_ESCOPO },
          signal,
        ),
      ),
    );
    return [...primeira.itens, ...demais.flatMap((pagina) => pagina.itens)];
  },

  async listInstalacoesPaginado(filtro, signal) {
    const payload = await request<{ items?: unknown; total?: unknown; temAna?: unknown; temInterno?: unknown }>('/instalacoes/paginado', {
      recurso: 'as instalações',
      query: {
        busca: filtro.busca?.trim() || undefined,
        ana: filtro.ana,
        interno: filtro.interno,
        limit: filtro.limit,
        offset: filtro.offset,
      },
      signal,
    });
    return {
      itens: toRows(payload?.items).map(toInstalacao) as Instalacao[],
      total: Number(payload?.total ?? 0),
      temAna: payload?.temAna === true,
      temInterno: payload?.temInterno === true,
    };
  },

  async listAgendamentos(signal) {
    const payload = await request<unknown>('/agendamentos', { recurso: 'os agendamentos', signal });
    return toRows(payload, 'instalacao').map(toAgendamento) as Agendamento[];
  },

  async listMeusAgendamentos(signal) {
    const payload = await request<unknown>('/agendamentos/meus', { recurso: 'os meus agendamentos', signal });
    return toRows(payload, 'instalacao').map(toAgendamento) as Agendamento[];
  },

  async listHistorico(signal, filtro) {
    const payload = await request<unknown>('/historico-atualizacoes', {
      recurso: 'o histórico de atualizações',
      query: { de: filtro?.de, ate: filtro?.ate },
      signal,
    });
    return toRows(payload, 'instalacao').map(toEventoHistorico) as EventoHistorico[];
  },

  async listNovidades(signal) {
    const payload = await request<unknown>('/novidades', { recurso: 'as novidades', signal });
    return toRows(payload).map(toNovidade) as Novidade[];
  },

  async criarNovidade(input: NovidadeInput, signal) {
    const payload = await request<unknown>('/novidades', {
      recurso: 'a criação da novidade',
      method: 'POST',
      body: input,
      signal,
    });
    return toNovidade((payload ?? {}) as Record<string, unknown>);
  },

  async excluirNovidade(id, signal) {
    await request<unknown>(`/novidades/${id}`, { recurso: 'a exclusão da novidade', method: 'DELETE', signal });
  },

  async listVersoes(instalacaoId, signal) {
    const payload = await request<unknown>('/agendamentos/versoes', {
      recurso: 'as versões disponíveis',
      query: { instalacaoId },
      signal,
    });
    return toVersoes(payload);
  },

  async listDisponibilidade(data, signal) {
    const payload = await request<unknown>('/agendamentos/disponibilidade', {
      recurso: 'a disponibilidade de horários',
      query: { data },
      signal,
    });
    return toDisponibilidade(payload, data) as HorarioDisponivel[];
  },

  async reterHorario(input, signal) {
    const payload = await request<unknown>('/agendamentos/retencoes', {
      recurso: 'a retenção do horário',
      method: 'POST',
      body: {
        agendado_para: montarAgendadoPara(input.data, input.hora),
        instalacoes_alvo: input.instalacoesAlvo,
      },
      signal,
    });
    const registro = (payload ?? {}) as Record<string, unknown>;
    return {
      holdId: String(registro.holdId ?? ''),
      ttlSegundos: Number(registro.ttlSegundos ?? 0),
      expiraEm: String(registro.expiraEm ?? ''),
      horarioRealInicio: String(registro.horarioRealInicio ?? input.hora),
      horarioRealFim: String(registro.horarioRealFim ?? input.hora),
    } satisfies RetencaoHorario;
  },

  async liberarRetencao(holdId, opcoes) {
    await request<unknown>(`/agendamentos/retencoes/${holdId}`, {
      recurso: 'a liberação da retenção do horário',
      method: 'DELETE',
      signal: opcoes?.signal,
      keepalive: opcoes?.melhorEsforco,
    });
  },

  async criarAgendamento(input: NovoAgendamento, signal) {
    if (input.versao === SEM_ATUALIZACOES_ID) {
      throw new Error('Não há atualizações disponíveis para a instalação selecionada.');
    }
    await request<unknown>('/agendamentos/grupos', {
      recurso: 'a criação do agendamento',
      method: 'POST',
      body: {
        versao: input.versao,
        agendado_para: montarAgendadoPara(input.data, input.hora),
        exec_only: false,
        instalacoes_alvo: input.instalacoesAlvo,
        instalacao_acesso: input.instalacaoAcesso,
        hold_id: input.holdId ?? null,
      },
      signal,
    });
  },

  async cancelarAgendamento(id, signal) {
    // Cancelar é uma transição de estado no backend, não um DELETE.
    await request<unknown>(`/agendamentos/${id}/transicoes/cancelar`, {
      recurso: 'o cancelamento do agendamento',
      method: 'POST',
      body: {},
      signal,
    });
  },

  async dispararAgendamentoAgora(id, signal) {
    await request<unknown>(`/agendamentos/${id}/transicoes/disparar-agora`, {
      recurso: 'o disparo imediato do agendamento',
      method: 'POST',
      body: {},
      signal,
    });
  },

  async buscarInstalacoesParaPersonificar(query, signal) {
    const payload = await request<unknown>('/personificacao/instalacoes', {
      recurso: 'a busca de instalações para personificar',
      query: { q: query },
      baseUrl: config.adminBaseUrl,
      signal,
    });
    const registro = (payload ?? {}) as { items?: unknown[] };
    const items = Array.isArray(registro.items) ? registro.items : [];
    return items
      .map((item) => {
        const linha = (item ?? {}) as Record<string, unknown>;
        return {
          id: linha.tenant_id != null ? String(linha.tenant_id) : '',
          nome: String(linha.nome ?? ''),
          codigo: linha.codigo != null ? String(linha.codigo) : null,
          instalacaoInfraId: linha.instalacao_id != null ? Number(linha.instalacao_id) : null,
          ana: linha.ana === true,
          interno: linha.interno === true,
        } satisfies InstalacaoBusca;
      })
      .filter((item) => item.id !== '');
  },

  async registrarPersonificacao(instalacaoInfraId, signal) {
    // Melhor esforço, de propósito: uma falha aqui é só um buraco na
    // auditoria, não motivo pra impedir o suporte de personificar de verdade.
    try {
      await request<unknown>(`/personificacao/instalacoes/${instalacaoInfraId}/auditoria`, {
        recurso: 'o registro de auditoria da personificação',
        method: 'POST',
        body: {},
        baseUrl: config.adminBaseUrl,
        signal,
      });
    } catch {
      // Sem re-throw — ver comentário acima.
    }
  },

  async abrirOuRenovarSessaoPersonificacao(instalacaoInfraId, signal) {
    // SEM try/catch: ao contrário da auditoria, aqui o erro (409 = já
    // personificada por outra pessoa) é o próprio propósito da chamada —
    // quem chama (diálogo ao confirmar, banner no heartbeat) decide o que
    // fazer com ele.
    await request<unknown>(`/personificacao/instalacoes/${instalacaoInfraId}/sessao`, {
      recurso: 'a sessão de personificação',
      method: 'POST',
      body: {},
      baseUrl: config.adminBaseUrl,
      signal,
    });
  },

  async encerrarSessaoPersonificacao(instalacaoInfraId, opcoes) {
    await request<unknown>(`/personificacao/instalacoes/${instalacaoInfraId}/sessao`, {
      recurso: 'o encerramento da sessão de personificação',
      method: 'DELETE',
      baseUrl: config.adminBaseUrl,
      signal: opcoes?.signal,
      keepalive: opcoes?.melhorEsforco,
    });
  },
};
